(() => {
  const initUpload = (root) => {
    if (root.dataset.uploadReady === 'true') return;
    root.dataset.uploadReady = 'true';

    const endpoint = (root.dataset.endpoint || '').trim();
    const maxMb = Number(root.dataset.maxMb || 50);
    const maxBytes = maxMb * 1024 * 1024;
    const extensions = (root.dataset.extensions || '').toLowerCase().split(',').map(v => v.trim()).filter(Boolean);

    const input = root.querySelector('[data-upload-input]');
    const dropzone = root.querySelector('[data-upload-dropzone]');
    const progress = root.querySelector('[data-upload-progress]');
    const status = root.querySelector('[data-upload-status]');
    const percent = root.querySelector('[data-upload-percent]');
    const bar = root.querySelector('[data-upload-bar]');
    const complete = root.querySelector('[data-upload-complete]');
    const completeName = root.querySelector('[data-upload-complete-name]');
    const replace = root.querySelector('[data-upload-replace]');
    const errorBox = root.querySelector('[data-upload-error]');
    const urlField = root.querySelector('[data-upload-url]');
    const filenameField = root.querySelector('[data-upload-filename]');
    const expiresField = root.querySelector('[data-upload-expires]');
    const form = root.closest('form');
    const submit = form?.querySelector('button[type="submit"]');

    let uploading = false;

    const setError = (message) => {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = !message;
    };

    const clearStoredFields = () => {
      [urlField, filenameField, expiresField].forEach((field) => {
        if (!field) return;
        field.value = '';
        field.disabled = true;
      });
    };

    const setProgress = (value, text) => {
      const bounded = Math.max(0, Math.min(100, Math.round(value)));
      if (progress) progress.hidden = false;
      if (bar) bar.style.width = `${bounded}%`;
      if (percent) percent.textContent = `${bounded}%`;
      if (status && text) status.textContent = text;
    };

    const setUploading = (value) => {
      uploading = value;
      if (submit) {
        submit.disabled = value;
        submit.setAttribute('aria-busy', value ? 'true' : 'false');
      }
    };

    const getExtension = (name) => {
      const match = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
      return match ? match[1] : '';
    };

    const formatExpiry = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' }).format(date);
    };

    const postJson = async (body) => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      let data = {};
      try { data = await response.json(); } catch (_) {}
      if (!response.ok) throw new Error(data.error || data.message || `Upload service returned ${response.status}`);
      return data;
    };

    const uploadToSignedUrl = (signedUrl, file, onProgress) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('x-upsert', 'false');
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress((event.loaded / event.total) * 100);
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`File upload failed (${xhr.status}). Please try again.`));
      });
      xhr.addEventListener('error', () => reject(new Error('The file could not be uploaded. Please check your connection and try again.')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled.')));
      const body = new FormData();
      body.append('cacheControl', '3600');
      body.append('', file);
      xhr.send(body);
    });

    const handleFile = async (file) => {
      if (!endpoint) {
        setError('Direct artwork upload is not connected yet. Please use the artwork link box below.');
        return;
      }
      if (!file) return;
      const ext = getExtension(file.name);
      if (!extensions.includes(ext)) {
        setError(`That file type is not supported. Please upload PDF, AI, EPS, PSD, SVG, PNG, JPG, TIFF or WebP.`);
        input.value = '';
        return;
      }
      if (file.size <= 0) {
        setError('That file appears to be empty. Please choose another file.');
        input.value = '';
        return;
      }
      if (file.size > maxBytes) {
        setError(`That file is larger than ${maxMb}MB. Please use the large/source artwork link below instead.`);
        input.value = '';
        return;
      }

      clearStoredFields();
      setError('');
      if (complete) complete.hidden = true;
      if (dropzone) dropzone.hidden = false;
      setUploading(true);
      setProgress(2, 'Preparing secure upload…');

      try {
        const signed = await postJson({
          action: 'sign-upload',
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream'
        });
        if (!signed.signedUrl || !signed.path) throw new Error('Upload service did not return a valid upload link.');

        setProgress(5, `Uploading ${file.name}…`);
        await uploadToSignedUrl(signed.signedUrl, file, (p) => setProgress(5 + (p * 0.85), `Uploading ${file.name}…`));
        setProgress(92, 'Creating your secure artwork link…');

        const finalised = await postJson({ action: 'finalize', path: signed.path, fileName: file.name });
        if (!finalised.downloadUrl) throw new Error('Your file uploaded, but the secure viewing link could not be created. Please try again.');

        if (urlField) {
          urlField.value = finalised.downloadUrl;
          urlField.disabled = false;
        }
        if (filenameField) {
          filenameField.value = file.name;
          filenameField.disabled = false;
        }
        if (expiresField) {
          expiresField.value = finalised.expiresAt || '';
          expiresField.disabled = !expiresField.value;
        }

        setProgress(100, 'Upload complete');
        setTimeout(() => { if (progress) progress.hidden = true; }, 250);
        if (dropzone) dropzone.hidden = true;
        if (complete) complete.hidden = false;
        if (completeName) completeName.textContent = file.name;
      } catch (error) {
        console.error('[POP 1 artwork upload]', error);
        if (progress) progress.hidden = true;
        if (dropzone) dropzone.hidden = false;
        setError(error?.message || 'Artwork upload failed. Please try again or use the artwork link box below.');
        clearStoredFields();
        input.value = '';
      } finally {
        setUploading(false);
      }
    };

    input?.addEventListener('change', () => handleFile(input.files?.[0]));

    ['dragenter', 'dragover'].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach((eventName) => dropzone?.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.remove('is-dragging');
    }));
    dropzone?.addEventListener('drop', (event) => handleFile(event.dataTransfer?.files?.[0]));

    replace?.addEventListener('click', () => {
      clearStoredFields();
      if (complete) complete.hidden = true;
      if (dropzone) dropzone.hidden = false;
      if (input) {
        input.value = '';
        input.click();
      }
    });

    form?.addEventListener('submit', (event) => {
      if (uploading) {
        event.preventDefault();
        setError('Please wait for your artwork to finish uploading before sending the quote request.');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Shopify/browser omits disabled controls from form submission. Re-enable
      // populated artwork fields immediately before submit as a final safeguard.
      [urlField, filenameField, expiresField].forEach((field) => {
        if (field && field.value) field.disabled = false;
      });

      const backupLink = form.querySelector('input[name="contact[Large / Source Artwork Link]"]');
      if (backupLink) backupLink.disabled = !backupLink.value.trim();
    });
  };

  const boot = () => document.querySelectorAll('[data-pop1-artwork-upload]').forEach(initUpload);
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
